/**
 * Auto-Save Hook
 * Automatically saves canvas state to IndexedDB with debouncing
 *
 * Phase 4 improvements:
 * - Fixed stale closure issue with refs
 * - Integrated with SyncContext for cloud uploads with retry
 * - Proper cleanup on unmount
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { saveAutoSave, type AutoSaveEntry } from '@/services/storage';
import { useSyncContextSafe } from '@/context/SyncContext';
import type { EditorSnapshot } from '@/types';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface UseAutoSaveOptions {
  /** User ID (if null, saves locally without cloud sync) */
  userId: string | null;
  /** Product ID */
  productId: string;
  /** Side (front or back) */
  side: 'front' | 'back';
  /** Canvas snapshot to save */
  snapshot: EditorSnapshot | null;
  /** Debounce delay in milliseconds (default: 10000 = 10 seconds) */
  debounceMs?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
}

export interface UseAutoSaveReturn {
  /** Current save status */
  status: SaveStatus;
  /** Manually trigger a save (bypasses debounce) */
  saveNow: () => Promise<void>;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Last error message */
  error: string | null;
}

/**
 * Auto-save hook with debouncing
 *
 * @example
 * ```tsx
 * const { status, saveNow } = useAutoSave({
 *   userId: user?.id ?? null,
 *   productId: product.id,
 *   side: activeSide,
 *   snapshot: getSnapshot(),
 * });
 * ```
 */
export function useAutoSave(options: UseAutoSaveOptions): UseAutoSaveReturn {
  const { userId, productId, side, snapshot, debounceMs = 10000, enabled = true } = options;

  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Use refs for values that change frequently to avoid stale closures
  const snapshotRef = useRef<EditorSnapshot | null>(snapshot);
  const userIdRef = useRef<string | null>(userId);
  const productIdRef = useRef<string>(productId);
  const sideRef = useRef<'front' | 'back'>(side);

  // SyncContext for cloud uploads with retry support
  const syncContext = useSyncContextSafe();

  // Keep refs in sync with props (fixes stale closure)
  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    productIdRef.current = productId;
  }, [productId]);

  useEffect(() => {
    sideRef.current = side;
  }, [side]);

  // Track mounted state for async cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Save function using refs to avoid stale closures
  const save = useCallback(
    async (isManual: boolean = false) => {
      const currentSnapshot = snapshotRef.current;
      const currentUserId = userIdRef.current;
      const currentProductId = productIdRef.current;
      const currentSide = sideRef.current;

      if (!currentSnapshot) {
        return;
      }

      if (isMountedRef.current) {
        setStatus('saving');
        setError(null);
      }

      try {
        // Use userId if available, otherwise use 'anonymous' for local-only saves
        const effectiveUserId = currentUserId || 'anonymous';

        const entry: AutoSaveEntry = {
          id: crypto.randomUUID(),
          userId: effectiveUserId,
          productId: currentProductId,
          side: currentSide,
          snapshot: currentSnapshot,
          createdAt: new Date(),
          isManualSave: isManual,
        };

        // Save to IndexedDB (local-first)
        await saveAutoSave(entry);

        // Upload to cloud via SyncContext (handles retry queue)
        if (currentUserId && syncContext) {
          // Queue for cloud upload - SyncContext handles retries
          syncContext.queueAutoSave(entry).catch((cloudError) => {
            // Log but don't fail - SyncContext handles retry
            console.warn('[useAutoSave] Cloud queue failed:', cloudError);
          });
        }

        if (isMountedRef.current) {
          setStatus('saved');
          setLastSaved(new Date());

          // Reset to idle after 2 seconds
          setTimeout(() => {
            if (isMountedRef.current) {
              setStatus('idle');
            }
          }, 2000);
        }
      } catch (err) {
        console.error('[useAutoSave] Save failed:', err);
        if (isMountedRef.current) {
          setStatus('error');
          setError(err instanceof Error ? err.message : 'Save failed');
        }
      }
    },
    [syncContext]
  );

  // Debounced auto-save
  useEffect(() => {
    // Don't auto-save if disabled or no snapshot (userId is optional for local-only saves)
    if (!enabled || !snapshot) {
      setStatus('idle');
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set status to pending (will save after debounce)
    setStatus('pending');

    // Schedule save after debounce period
    timeoutRef.current = setTimeout(() => {
      save(false);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, userId, snapshot, debounceMs, save]);

  // Manual save function
  const saveNow = useCallback(async () => {
    // Clear pending debounce
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    await save(true);
  }, [save]);

  return {
    status,
    saveNow,
    lastSaved,
    error,
  };
}
